/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { MissionSpecCard } from "models/MissionSpec";
import { FC, useMemo } from "react";
import Card from "views/Mission/TeamSpecV2/components/Card";
import actionIcon from "views/Mission/TeamSpecV2/assets/quick-action.svg";
import { useHistory } from "react-router-dom";
import StatusButton from "views/HomeV2/StatusButton";
import { countActiveRoles, countOpenRoles } from "views/HomeV2/utils";
import { MissionRoleStatus } from "@a_team/models/dist/MissionRole";
import {
  CollaboratorAvatar,
  ShowCount,
} from "views/Mission/TeamSpecV2/ProjectName/CollaboratorsButton";
import { MissionStatus } from "@a_team/models/dist/MissionObject";
import { pluralizeString } from "helpers/strings";
import { useRootStore } from "store";

const getCSSRules: CSSRulesResolver = ({ colors }) => ({
  titleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  title: {
    fontSize: 24,
    lineHeight: "28.8px",
    fontWeight: 800,
    margin: 0,
  },
  linkArrow: {
    marginLeft: 8,
    fontSize: 30,
    opacity: 0,
    transform: "translateX(-10px)",
    transition: "opacity 0.3s ease, transform 0.2s ease",
  },
  card: {
    "&:hover .linkArrow": {
      opacity: 1,
      transform: "translateX(0px)",
    },
  },

  cardInner: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    padding: 8,
    gap: 16,
  },

  underTitle: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",

    "@media (max-width: 599px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 16,
    },
  },

  icon: {
    display: "flex",
    width: 56,
    height: 56,
    backgroundColor: colors.Grey[100],
    flexShrink: 0,
    borderRadius: 19.2,
    alignItems: "center",
    justifyContent: "center",
  },
  withImage: {
    width: 56,
    height: 56,
    borderRadius: 19.2,
  },
  withIcon: {
    width: 16,
    height: 16,
  },
  col: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  avatarList: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap" as const,
  },
  avatar: {
    objectFit: "cover",
    width: 40,
    height: 40,
    border: "2px solid white",
    marginRight: -5,
  },
  roleLabel: {
    fontSize: 12,
    lineHeight: "18px",
    fontWeight: 400,
    color: colors.Grey[500],
  },
  roleCount: {
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 500,
  },
});

const MissionCard: FC<{
  mission?: MissionSpecCard;
}> = ({ mission }) => {
  const {
    userStore: { user },
  } = useRootStore();
  const styles = useCSSRulesWithTheme(getCSSRules);
  const history = useHistory();
  const isAdmin = user?.isAdmin;
  const { proposals } = mission || {};

  // We should link to proposals if the latest proposal is awaiting review
  const linkToProposals = useMemo(() => {
    if (!proposals?.length) {
      return false;
    }
    const latestProposal = proposals.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];

    if (isAdmin) {
      return (
        latestProposal.adminShouldReview || latestProposal.clientShouldReview
      );
    }
    return latestProposal.clientShouldReview;
  }, [proposals, isAdmin]);

  const activeRoles = (mission?.roles || []).filter(
    (role) => role.status === MissionRoleStatus.Active
  );

  const avatarsToShow = activeRoles.length > 3 ? 2 : activeRoles.length;
  const showCount = activeRoles.length > 3;

  const avatarList = activeRoles
    .filter((role) => role?.user?.profilePictureURL)
    .slice(0, avatarsToShow);

  const isActive = [
    MissionStatus.Running,
    MissionStatus.ScheduledToEnd,
  ].includes(mission?.platformStatus as MissionStatus);

  const activeRoleCount = countActiveRoles(mission?.roles);
  const openRoleCount = countOpenRoles(mission?.roles);
  const cardHref = `/mission/${mission?.mid}${
    isActive ? "/time-tracking" : ""
  }`;
  const buttonHref = linkToProposals
    ? `/mission/${mission?.mid}${linkToProposals ? "/proposals" : ""}`
    : cardHref;

  const onCardClick = () => {
    history.push(cardHref);
  };

  return (
    <Card onClick={onCardClick} css={styles.card}>
      <div css={styles.cardInner} data-testing-mission-name={mission?.title}>
        <div css={styles.titleContainer}>
          <h3 css={styles.title}>{mission?.title}</h3>
          <span css={styles.linkArrow} className="linkArrow">
            &rarr;
          </span>
        </div>

        <div css={styles.underTitle}>
          {/** Awaiting launch */}
          {!isActive && (
            <div css={styles.col}>
              <div css={styles.row}>
                <div css={styles.icon}>
                  <img
                    css={css(styles.withIcon)}
                    src={actionIcon}
                    alt="Role icon"
                  />
                </div>
              </div>
              <div css={styles.row}>
                <p css={styles.roleLabel}>Roles</p>
                <p css={styles.roleCount}>
                  {pluralizeString(openRoleCount, "role", "roles")} open
                </p>
              </div>
            </div>
          )}

          {/** Active projects */}
          {isActive && (
            <div css={styles.col}>
              {avatarList?.length > 0 && (
                <div css={styles.row}>
                  <div css={css(styles.avatarList)}>
                    {avatarList?.map(({ user }) => (
                      <CollaboratorAvatar
                        key={user?.uid}
                        profilePictureURL={user?.profilePictureURL as string}
                        size="md"
                        css={styles.avatar}
                      />
                    ))}
                    {showCount && (
                      <ShowCount
                        count={activeRoles.length - avatarsToShow}
                        css={styles.avatar}
                        size={42}
                      />
                    )}
                  </div>
                </div>
              )}
              {avatarList?.length === 0 && (
                <div css={styles.row}>
                  <div css={styles.icon}>
                    <img
                      css={css(styles.withIcon)}
                      src={actionIcon}
                      alt="Role icon"
                    />
                  </div>
                </div>
              )}
              <div css={styles.row}>
                <p css={styles.roleLabel}>Members</p>
                <p css={styles.roleCount}>
                  {pluralizeString(
                    activeRoleCount,
                    "active member",
                    "active members"
                  )}
                </p>
              </div>
            </div>
          )}

          {/** Status button */}
          <div css={styles.col}>
            <StatusButton
              mission={mission}
              linkToProposals={linkToProposals}
              href={buttonHref}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default MissionCard;
